import React, { useState, useEffect, useRef } from 'react';
import { useFormContext } from '../../../Components/Form/Provider';
import { classNames } from '../../../Services/helpers';
import Button from '../../../Components/Button/Button';
import ButtonRow from '../../../Components/Button/ButtonRow';
import Toggle from '../../../Components/Button/Toggle';
import { useKeyboardHelper } from '../../../Components/Mobile';
import { replyDraftStorageKey } from './replyDraft';
import EmojiSuggestions, { useEmojiSuggestions } from '../../../Components/Emoji';

const ConversationReplyBox = ({showAttachments, setShowAttachments, conversationId, canAddMessages}) => {
    const { formData, updateField, isSubmitting, formDisabled, setFormData } = useFormContext();
    const fieldRef = useRef();
    const {hideNav, showNav} = useKeyboardHelper(`admin-conversation-reply-${conversationId}`);
    const loadedConversationIdRef = useRef(null);
    const [textAreaHeight, setTextAreaHeight] = useState('50px');
    const value = formData['message'] ?? '';
    const isPrivate = formData['private'] ?? false;
    const isWaiting = formData['waiting'] ?? false;

    const storageKey = replyDraftStorageKey(conversationId);

    useEffect(() => {
        if (!conversationId) {
            loadedConversationIdRef.current = null;
            return;
        }

        if (loadedConversationIdRef.current === conversationId) {
            return;
        }

        loadedConversationIdRef.current = conversationId;

        const savedMessage = localStorage.getItem(storageKey);
        if (savedMessage && savedMessage.trim() && !formData['message']) {
            setFormData(prev => ({ ...prev, message: savedMessage }));
        }
    }, [conversationId, storageKey, setFormData, formData]);

    useEffect(() => {
        if (!fieldRef?.current) {
            return;
        }
        if (value === null || value === undefined || value === '') {
            setTextAreaHeight('50px');
            return;
        }
        setTextAreaHeight(Math.max(70, fieldRef.current.scrollHeight) + 'px');
    }, [value]);

    useEffect(() => {
        if (!conversationId) {
            return;
        }

        if (value && value.trim()) {
            localStorage.setItem(storageKey, value);
        } else {
            localStorage.removeItem(storageKey);
        }

        window.dispatchEvent(new CustomEvent('conversationReplyStorageChange', {
            detail: { conversationId, hasSavedReply: !!(value && value.trim()) }
        }));
    }, [value, conversationId, storageKey]);


    // Private and Waiting are mutually exclusive: a private note never reaches the end-user, so there
    // is nothing for them to reply to. Turning either on turns the other off.
    const handlePrivateToggle = () => {
        const nextPrivate = !isPrivate;

        updateField('private', nextPrivate);

        if (nextPrivate && isWaiting) {
            updateField('waiting', false);
        }
    };

    const handleWaitingToggle = (nextWaiting) => {
        updateField('waiting', nextWaiting);

        if (nextWaiting && isPrivate) {
            updateField('private', false);
        }
    };

    const emoji = useEmojiSuggestions({
        value,
        onChange: (next) => updateField('message', next),
        inputRef: fieldRef,
    });

    const isDisabled = formDisabled || isSubmitting;
    const privateLabel = isPrivate ? 'Private message (only visible to admin and AI)' : 'Make message private';

    return (
        <div className="flex items-start space-x-4">
                <div className="min-w-0 flex-1">
                    <div className="relative">
                        <div className={classNames(
                            'overflow-hidden rounded-lg shadow-xs ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus-within:ring-2 focus-within:ring-brand dark:focus-within:ring-brand-light',
                            isDisabled && 'bg-slate-50',
                            isPrivate && 'bg-slate-100 dark:bg-slate-800'
                        )}>
                            <label htmlFor="conversation-message" className="sr-only">
                                Message
                            </label>
                            <textarea
                                ref={fieldRef}
                                rows={2}
                                name="message"
                                id="conversation-message"
                                style={{height: textAreaHeight}}
                                className="block w-full resize-none border-0 py-1.5 text-slate-900 dark:text-slate-100 bg-transparent focus:ring-0 sm:text-sm sm:leading-6"
                                placeholder={canAddMessages ? 'Message...' : 'Messages cannot be added to this conversation'}
                                value={value}
                                onChange={emoji.handleChange}
                                onKeyDown={emoji.handleKeyDown}
                                onFocus={hideNav}
                                onBlur={() => {
                                    emoji.close();
                                    showNav();
                                }}
                                disabled={isDisabled}
                            />

                            <div className="py-2" aria-hidden="true">
                                <div className="py-px">
                                    <div className="h-9" />
                                </div>
                            </div>
                        </div>

                        <EmojiSuggestions {...emoji} />

                        <div className="absolute inset-x-0 bottom-0 flex justify-between py-2 pl-3 pr-2">
                            <div className="flex items-center gap-2">
                                <ButtonRow arrange="right">
                                    <Button
                                        icon="Paperclip"
                                        iconTheme={showAttachments ? "solid" : "light"}
                                        theme={showAttachments ? "primaryFlush" : "lightFlush"}
                                        onClick={() => setShowAttachments(!showAttachments)}
                                        zeroMargin
                                        flush
                                    />
                                    <Button
                                        icon="Lock"
                                        iconTheme={isPrivate ? "solid" : "light"}
                                        theme={isPrivate ? "primaryFlush" : "lightFlush"}
                                        onClick={handlePrivateToggle}
                                        zeroMargin
                                        flush
                                        ariaLabel={privateLabel}
                                        title={privateLabel}
                                    />
                                </ButtonRow>
                            </div>
                            <div className="shrink-0 flex items-center gap-3">
                                <Toggle
                                    className="shrink-0"
                                    enabled={isWaiting}
                                    onChange={handleWaitingToggle}
                                    label="Waiting"
                                    ariaLabel="Waiting"
                                    disabled={isDisabled}
                                    hideFocusRing
                                />
                                <ButtonRow>
                                    <Button theme="primary"
                                        loading={isSubmitting}
                                        disabled={isSubmitting || isDisabled}
                                        label="Send"
                                        type="submit"
                                        zeroMargin
                                    />
                                </ButtonRow>
                            </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConversationReplyBox;
